import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import Logo from "./Logo";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView({ behavior: "smooth" });
};

const MaskLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-[0.12em]">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const trust = [
  "Ph.D., Curriculum & Instruction",
  "3x President's Club",
  "Top SDR Award Winner",
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBlob = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden grain"
      style={{ background: "var(--hero-bg)" }}
    >
      <div className="absolute inset-0 hero-glow" />
      <div className="absolute inset-0 hero-grid" />
      <motion.div style={{ y: yBlob }} className="absolute -top-24 -left-20 w-[38rem] h-[38rem] rounded-full opacity-40" >
        <div className="w-full h-full rounded-full animate-blob" style={{ background: "radial-gradient(circle,#E933C6,transparent 65%)", filter: "blur(80px)" }} />
      </motion.div>
      <motion.div style={{ y: yBlob }} className="absolute top-40 -right-24 w-[32rem] h-[32rem] rounded-full opacity-40">
        <div className="w-full h-full rounded-full animate-blob" style={{ background: "radial-gradient(circle,#8B5CF6,transparent 65%)", filter: "blur(80px)", animationDelay: "-6s" }} />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 sm:pt-40 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-7"
            >
              <Logo variant="dark" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.6 }}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full mb-6 glass-dark"
            >
              <Sparkles size={13} className="text-fuchsia-400" />
              <span className="font-mono-accent text-[10px] sm:text-xs tracking-[0.22em] uppercase text-fuchsia-200">
                AI Consulting · South Carolina & Remote
              </span>
            </motion.div>

            <h1 className="font-display font-extrabold text-white tracking-tight text-4xl sm:text-6xl lg:text-7xl leading-[1.05]">
              <MaskLine delay={0.25}>AI your team will</MaskLine>
              <MaskLine delay={0.38}>
                <span className="text-gradient">actually use.</span>
              </MaskLine>
            </h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-6 max-w-xl text-base sm:text-lg text-slate-300/90 leading-relaxed"
            >
              I help growing businesses and nonprofits put AI to work: chatbots that answer
              customers, simple automations that save hours, and hands-on training so your team
              isn't left to "figure it out." Built and taught by a former educator with a Ph.D. in
              Curriculum & Instruction.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.6 }}
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              <button
                data-testid="hero-book-button"
                onClick={() => scrollTo("#contact")}
                className="btn-glow inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm sm:text-base font-semibold text-white"
                style={{ background: "linear-gradient(100deg,#FF4FD8,#E933C6 55%,#8B5CF6)" }}
              >
                Book a Free Consultation <ArrowRight size={18} />
              </button>
              <button
                data-testid="hero-explore-button"
                onClick={() => window.dispatchEvent(new Event("open-elevatebot"))}
                className="inline-flex items-center gap-2 rounded-xl px-6 py-3.5 text-sm sm:text-base font-semibold text-white border border-white/15 hover:border-fuchsia-400/50 hover:bg-white/5 transition-colors"
              >
                Try My Chatbot →
              </button>
            </motion.div>

            <motion.ul
              initial="hidden"
              animate="show"
              variants={{ show: { transition: { staggerChildren: 0.12, delayChildren: 1 } } }}
              className="mt-10 flex flex-col sm:flex-row flex-wrap gap-x-6 gap-y-2"
            >
              {trust.map((t) => (
                <motion.li
                  key={t}
                  variants={{ hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }}
                  className="flex items-center gap-2 text-xs sm:text-[13px] text-slate-400"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-400" />
                  {t}
                </motion.li>
              ))}
            </motion.ul>
          </div>

          {/* Right: floating glass accent card w/ parallax */}
          <div className="lg:col-span-5 relative hidden lg:block">
            <motion.div
              style={{ y: yCard }}
              initial={{ opacity: 0, scale: 0.9, rotate: -3 }}
              animate={{ opacity: 1, scale: 1, rotate: -3 }}
              transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="glass-dark rounded-3xl p-6 relative overflow-hidden">
                <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full" style={{ background: "radial-gradient(circle,#E933C6,transparent 70%)", filter: "blur(30px)" }} />
                <div className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-fuchsia-300 mb-4">
                  live_system.status
                </div>
                {[
                  { k: "Chatbot uptime", v: "24 / 7" },
                  { k: "Response time", v: "< 3 sec" },
                  { k: "Human oversight", v: "100%" },
                ].map((row) => (
                  <div key={row.k} className="flex items-center justify-between py-3 border-b border-white/10 last:border-0">
                    <span className="text-sm text-slate-400">{row.k}</span>
                    <span className="font-display font-bold text-white text-lg">{row.v}</span>
                  </div>
                ))}
                <div className="mt-5 rounded-2xl bg-black/40 border border-white/10 p-4">
                  <p className="text-[13px] text-slate-300 leading-relaxed">
                    <span className="text-fuchsia-300 font-semibold">ElevateBot:</span> Hi! I can answer
                    questions and book you a time — 24/7. Want to see how?
                  </p>
                </div>
              </div>
              <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -left-8 -bottom-8 glass-dark rounded-2xl px-5 py-4"
              >
                <div className="font-display text-2xl font-extrabold text-gradient">Endless.</div>
                <div className="text-[11px] text-slate-400">The possibilities?</div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* transition fade into cream body */}
      <div className="absolute bottom-0 inset-x-0 h-32 z-[2]" style={{ background: "linear-gradient(to bottom, transparent, var(--body-bg))" }} />
    </section>
  );
};

export default Hero;
